import React, { useState, useEffect, useRef } from 'react';
import { FiPaperclip, FiMail, FiBookmark, FiMessageCircle } from 'react-icons/fi';
import { FaWhatsapp, FaBookmark as FaBookmarkFilled } from 'react-icons/fa';
import SkillBadge from '../../../common/skillBadge';
import ResumeModal from '../../../common/resumeModal';
import EmailModal from '../../../common/emailModal';
import { generateRequestId, getInitials } from '../../../common/common';
import { RecruiterApi } from '../../../supports/recruiterApi';
import CommentModal from '../../../common/candidateCommentModal';
import { useToast } from '../../../common/toastSystem';
import { FileText } from "lucide-react";

// SkillsSection component with show more/less functionality
const SkillsSection = ({ skills }) => {
  const [showAllSkills, setShowAllSkills] = useState(false);
  const skillsArray = Array.isArray(skills) ? skills : [];
  const visibleSkills = showAllSkills ? skillsArray : skillsArray.slice(0, 3);
  const hiddenCount = skillsArray.length - 3;

  return (
    <div>
      <p className="mt-2.5 text-xs text-gray-999">Skills & Expertise</p>
      <div className="flex flex-wrap gap-2 mt-2 items-center">
        {visibleSkills.map((skill, idx) => (
          <SkillBadge key={`${skill}-${idx}`}>{skill}</SkillBadge>
        ))}

        {hiddenCount > 0 && !showAllSkills && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowAllSkills(true);
            }}
            className="text-xs px-2 py-1 rounded-full border border-gray-200 cursor-pointer hover:bg-gray-100 "
          >
            +{hiddenCount} more
          </button>
        )}

        {showAllSkills && hiddenCount > 0 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowAllSkills(false);
            }}
            className="text-xs px-2 py-1 rounded-full border border-gray-200 cursor-pointer hover:bg-gray-100"
          >
            Show less
          </button>
        )}
      </div>
    </div>
  );
};

const SimilarCandidateCard = ({ candidate, pageType, onClick, jobId, jobDetails,searchType,onUpdateCandidate }) => {
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [resumeUrl, setResumeUrl] = useState(null);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [resumeError, setResumeError] = useState('');
  const [resumeFileType, setResumeFileType] = useState(null);
  const abortControllerRef = useRef(null);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [comment, setComment] = useState('');
  const [savedComment, setSavedComment] = useState('');
  const [isBookmarked, setIsBookmarked] = useState(!!candidate.is_tagged);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);
  const toast = useToast()
  const [candidateData, setCandidateData] = useState(candidate)
  const handleOpenResume = async (e) => {
    e.stopPropagation();
    setResumeError('');
    setResumeLoading(true);
    setShowResumeModal(true);
    const controller = new AbortController();
    abortControllerRef.current = controller;
    try {
      const response = await RecruiterApi.getResumePreview({
        user_payload: { request_id: generateRequestId() },
        candidate_id: candidate.id
      }, controller.signal);
      setResumeLoading(false);
      if (response.success) {
        setResumeUrl(response.data.sas_token_url)
        setResumeFileType(response.data.filetype || null);
      } else {
        setResumeUrl(null)
        setResumeFileType(null);
      }
    } catch (err) {
      setResumeError('Failed to load resume. Please try again.');
      setResumeUrl(null);
      setResumeFileType(null);
    } finally {
      setResumeLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const roundedLlmScore =
    typeof candidate.llm_score === 'number'
      ? candidate.llm_score.toFixed(2)
      : candidate.llm_score;

  // Only close the modal locally, do not notify parent or pass candidate
  const handleCloseResume = () => {
    setShowResumeModal(false);
    setResumeUrl(null);
    setResumeError('');
    setResumeFileType(null);
  };

  const handleOpenEmail = (e) => {
    e.stopPropagation();
    setShowEmailModal(true);
    setEmailSent(false);
    setEmailError('');
  };

  const handleCloseEmail = () => {
    setShowEmailModal(false);
    setEmailSent(false);
    setEmailError('');
  };

  const handleSendEmail = async (body) => {
    setSendingEmail(true);
    setEmailError('');
    try {
      const response = await RecruiterApi.sendMail(body)

      if (response.success) {
        if (response.data) {
          candidate.is_email_sent = true;
          candidate.email_sent_at = response?.data.email_sent_at || null;
          setCandidateData((prev) => ({
            ...prev,
            is_email_sent: true,
            email_sent_at: response?.data.email_sent_at || null,
          }));
          if(onUpdateCandidate){
            onUpdateCandidate(candidate);
          }
          toast.success(response.message || 'Email sent successfully');
        }
      } else {
        toast.warning(response.message || 'Failed to send email. Please try again.');
      }
      setEmailSent(true);
    } catch (err) {
      setEmailError('Failed to send email. Please try again.');
    } finally {
      setShowEmailModal(false);
      setSendingEmail(false);
    }
  };

  // Handler for clicking the card
  const handleCardClick = () => {
    onClick(candidate.id);
  };

  const handleOpenComment = () => {
    setShowCommentModal(true);
    setComment(savedComment || '');
  };

  const handleCloseComment = () => {
    setShowCommentModal(false);
  };

    const handleUpdateComment = (updatedData) => {
      candidate.comment_count = updatedData?.updatedCount
      
  };


  const handleSaveComment = () => {
    setSavedComment(comment);
    setShowCommentModal(false);
  };

  const handleBookmarkClick = async (e) => {
    e.stopPropagation();
    if (bookmarkLoading) return;
    setBookmarkLoading(true);

    try {
      let requestBody = {
        payload: {
          candidate_id: candidate.id,
          job_id: jobId,
          is_tagged: !isBookmarked,
        },
        user_payload: {
          request_id: generateRequestId()
        }
      }
      const response = await RecruiterApi.bookmarkCandidate?.(requestBody);
      if (response && response.success) {
        setIsBookmarked((prev) => !prev);
        candidate.is_tagged = !candidate.is_tagged
      } else {
        toast.warning('Failed to tag. please try again later');
      }
    } catch (error) {
      toast.warning('Failed to tag. please try again later');
    } finally {
      setBookmarkLoading(false);
    }
  };

  return (
    <>
      <div
        className="bg-white p-4 rounded-xl border border-gray-200 cursor-pointer hover:shadow"
        onClick={handleCardClick}
        data-candidate-id={candidate.id}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {candidate.avatar ? (
              <img src={candidate.avatar} alt={candidate.name} className="w-12 h-12 rounded-full" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mph-primary font-bold text-lg">
                {getInitials( candidate?.full_name ?? `${candidate.first_name || ""} ${candidate.last_name || ""}`)}
              </div>
            )}          <div>
              <div className='flex items-center gap-2'><h4 className="font-bold text-sm text-gray-333">{
              candidate?.full_name ?? `${candidate.first_name || ""} ${candidate.last_name || ""}`
}</h4>
                <SkillBadge key="active">
                {candidate.crm_status ? candidate.crm_status : "Active"}
                </SkillBadge>
              </div>
              <p className="text-xs text-gray-999">{candidate.highest_position}</p>
            </div>
          </div>
          {(pageType === 'matching' || searchType=='dropdown') && (
            <div className="text-center">
              <p className="font-bold profiler">{roundedLlmScore}%</p>
              <p className="text-xs profiler">Match</p>
            </div>
          )}
        </div>
        <div className="text-xs space-y-2 mt-3 pl-13">
          <div className="grid grid-cols-2">
            <span className="text-gray-999">Email ID</span>
            <span className="font-semibold text-gray-333">{candidate.email || '-'}</span>
          </div>
          <div className="grid grid-cols-2">
            <span className="text-gray-999">Contact number</span>
            <span className="font-semibold text-gray-333">{candidate.phone || '-'}</span>
          </div>
          <div className="grid grid-cols-2">
            <span className="text-gray-999">Gender</span>
            <span className="font-semibold text-gray-333">{candidate.gender || '-'}</span>
          </div>
          <div className="grid grid-cols-2">
            <span className="text-gray-999">Location</span>
            <span className="font-semibold text-gray-333">{candidate.location || candidate.city || '-'}</span>
          </div>
        </div>
        {/* <SkillsSection skills={candidate.skills} /> */}
        <div className='mt-3 border-t border-gray-100'>
          <div className="flex justify-between mt-3">
            <div className='flex items-center gap-2'>
              <button
                className="h-7 w-7 flex items-center justify-center rounded-full border mph-primary"
                onClick={e => { e.stopPropagation(); handleOpenResume(e); }}
                title="Preview Resume"
                type="button"
              >
                {/* <FiPaperclip size={14} /> */}
               <FileText size={18} className='mph-primary' />
              </button>
              {(pageType === 'matching' || searchType=='dropdown') && (
                <button
                  className="h-7 w-7 flex items-center justify-center rounded-full border mph-primary"
                  onClick={e => { e.stopPropagation(); handleOpenEmail(e); }}
                  title={
                      candidate?.generic_email_sent_at || candidate?.email_sent_at
                        ? `Last email sent on ${new Date(
                            candidate?.generic_email_sent_at ?? candidate?.email_sent_at
                          ).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`
                        : "Send Email"
                    }
                  type="button"
                >
                  <FiMail size={14} />
                </button>
              )}
              {/* <button
                className="h-7 w-7 flex items-center justify-center rounded-full border text-green-500"
                onClick={e => e.stopPropagation()}
                type="button"
              >
                <FaWhatsapp size={14} />
              </button> */}
              <div className='relative inline-block'>
    <button
                className="h-7 w-10 flex items-center justify-center rounded-full border mph-primary"
                onClick={e => { e.stopPropagation(); handleOpenComment(e); }}
                type="button"
                title="Comments"
              >
                  <div className="flex items-center gap-1 px-4 py-2 space-between">
                             <FiMessageCircle size={14} className="mph-primary" />
                         
                           </div>
              </button>
                    {/* Badge */}
                {candidate.comment_count > 0 && (
                  <span className="absolute -top-2 -right-2 bg-emerald-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-semibold">
                    {candidate.comment_count}
                  </span>
                )}
              </div>
          
            </div>
            {(pageType === 'matching' || searchType=='dropdown') && (
              <button
                className={`h-7 w-7 flex items-center justify-center rounded-full border mph-primary transition-colors`}
                onClick={handleBookmarkClick}
                disabled={bookmarkLoading}
                title={isBookmarked ? "Remove tag" : "Tag candidate"}
                aria-label={isBookmarked ? "Remove tag" : "Tag candidate"}
                tabIndex={0}
              >
                {bookmarkLoading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin h-4 w-4 text-gray-400 inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                  </span>
                ) : isBookmarked ? (
                  <FaBookmarkFilled className="mph-primary" size={16} />
                ) : (
                  <FiBookmark className="mph-primary" size={16} />
                )}
              </button>
            )}
          </div>
        </div>
      </div>
      <CommentModal
        isOpen={showCommentModal}
        comment={comment}
        setComment={setComment}
        onSave={handleSaveComment}
        onClose={handleCloseComment}
        onUpdateCandidate={handleUpdateComment}
        candidateId={candidate.id}
      />
      {showResumeModal && (
        <ResumeModal
          isOpen={showResumeModal}
          onClose={handleCloseResume}
          title={[candidate?.full_name ?? `${candidate.first_name || ""} ${candidate.last_name || ""}`]
          .filter(Boolean)
          .join(' ') + ' - Resume'}
          resumeUrl={resumeUrl}
          loading={resumeLoading}
          error={resumeError}
          filetype={resumeFileType}
        />
      )}
      {showEmailModal && (
        <EmailModal
          isOpen={showEmailModal}
          onClose={handleCloseEmail}
          candidateName={candidate?.full_name ?? `${candidate.first_name || ""} ${candidate.last_name || ""}`
}
          candidateEmail={candidate.email}
          candidateId={candidate?.id}
          jobDuration={jobDetails?.mph_contractduration || jobDetails.contract_duration || ""}
          jobTitle={jobDetails?.title || ""}
          jobOpeningDate={jobDetails?.mph_expctdstrtdate || jobDetails.expected_start_date || ""}
          jobLocation={jobDetails?.mph_city || jobDetails.city || ""}
          isEmailSent={
            candidateData?.is_email_sent ||
            Boolean(
              candidateData?.email_sent_at ??
              candidate?.email_sent_at ??
              candidateData?.generic_email_sent_at ??
              candidate?.generic_email_sent_at,
            ) ||
            false
          }
          mphJobId = { jobDetails.mph_jobid}
          emailSentOn={
            candidateData?.email_sent_at ??
            candidate?.email_sent_at ??
            candidateData?.generic_email_sent_at ??
            candidate?.generic_email_sent_at ??
            ""
          }
          emailSentBy={
            candidateData?.email_sent_by ??
            candidate?.email_sent_by ??
            candidateData?.generic_email_sent_by ??
            candidate?.generic_email_sent_by ??
            null
          }
          emailSentByFirstName={
            candidateData?.email_sent_by_first_name ??
            candidate?.email_sent_by_first_name ??
            candidateData?.generic_email_sent_by_first_name ??
            candidate?.generic_email_sent_by_first_name ??
            null
          }
          emailSentByLastName={
            candidateData?.email_sent_by_last_name ??
            candidate?.email_sent_by_last_name ??
            candidateData?.generic_email_sent_by_last_name ??
            candidate?.generic_email_sent_by_last_name ??
            null
          }
          emailSentByEmail={
            candidateData?.email_sent_by_email ??
            candidate?.email_sent_by_email ??
            candidateData?.generic_email_sent_by_email ??
            candidate?.generic_email_sent_by_email ??
            null
          }
          jobId={jobDetails?.id || ""}
          onSend={(data) => {
            handleSendEmail(data);
          }}
          sending={sendingEmail}
          sent={emailSent}
          error={emailError}
        />
      )}
    </>
  );
};

export default SimilarCandidateCard;